import React, { useEffect } from "react";
import { ISurveyQuestion } from "../../../models/survey/survey-question.model";
import modelPartsSvg from "../../../assets/images/survey-custom/model_parts.svg";
import SurveyAnswerCustomMap, { IMapConfig } from "./survey-answer-custom-map";

export interface ISurveyAnswerBodyPartsProps {
    question: ISurveyQuestion;
    value: string | number | Array<string | number> | null;
    onChange: (value: string | number | Array<string | number>) => void;
    onSave?: (value: string | number | Array<string | number>, shouldClose?: boolean) => void;
}

// Mapowanie części ciała do id w SVG (na podstawie BODYMAP_PARTS z HTML)
// Uwaga: niektóre id mogą nie istnieć w SVG - będą ignorowane podczas budowania overlay
const BODY_PARTS: Array<{ key: string; ids: string[]; label: string }> = [
    { key: "NOGI", ids: ["legs_left_upper", "legs_left_lower", "legs_right_upper", "legs_right_lower"], label: "NOGI" },
    { key: "RAMIONA", ids: ["arms_left", "arms_right"], label: "RAMIONA" },
    { key: "BRZUCH", ids: ["abdomen"], label: "BRZUCH" },
    { key: "BIUST", ids: ["bust"], label: "BIUST" },
    { key: "BIODRA", ids: ["hips_left", "hips_right"], label: "BIODRA" },
    { key: "TALIA", ids: ["waist"], label: "TALIA" }, // Może nie istnieć w SVG
    { key: "UDA", ids: ["thighs_left", "thighs_right", "legs_left_upper", "legs_right_upper"], label: "UDA" }, // Użyj części nóg jako fallback
    { key: "RĘCE", ids: ["hand_left_upper", "hand_left_lower", "hand_right_upper", "hand_right_lower"], label: "RĘCE" },
    { key: "DŁONIE", ids: ["hands_left", "hands_right", "hand_left_lower", "hand_right_lower"], label: "DŁONIE" }, // Użyj części rąk jako fallback
];

const mapConfig: IMapConfig = {
    type: "id",
    regions: BODY_PARTS.map(part => ({
        key: part.key,
        id: part.ids,
        label: part.label,
    })),
    svgStyle: {
        fill: "#ffffff",
        stroke: "rgba(42, 30, 47, 0.42)",
        strokeWidth: 2,
    },
};

const SurveyAnswerBodyParts: React.FC<ISurveyAnswerBodyPartsProps> = ({
    question,
    value,
    onChange,
    onSave,
}) => {
    // Znajdź optionId dla "NIC NIE CHCĘ PODKREŚLAĆ"
    const nothingOptionId = React.useMemo(() => {
        const options = Array.isArray(question.options) ? question.options : [];
        const nothingOption = options.find(opt => 
            opt.label && (opt.label.toUpperCase().includes("NIC NIE CHCĘ PODKREŚLAĆ") || opt.label.toUpperCase().includes("ŻADNA"))
        );
        return nothingOption ? (nothingOption.id ?? nothingOption.value ?? null) : null;
    }, [question.options]);

    // Gdy komponent startuje i nic nie jest zaznaczone – domyślnie zaznacz "NIC NIE CHCĘ PODKREŚLAĆ" lub "ŻADNA"
    const isEmpty = value === null || value === undefined || (Array.isArray(value) && value.length === 0);
    useEffect(() => {
        if (isEmpty && nothingOptionId !== null) {
            onChange([nothingOptionId]);
        }
    }, [isEmpty, nothingOptionId, onChange]);

    const handleChange = (newValue: string | number | Array<string | number>) => {
        const nothingStr = nothingOptionId !== null ? String(nothingOptionId) : null;
        
        // Gdy wszystko odznaczone → ustaw "NIC NIE CHCĘ PODKREŚLAĆ" lub "ŻADNA"
        if (Array.isArray(newValue) && newValue.length === 0) {
            if (nothingOptionId !== null) {
                onChange([nothingOptionId]);
            }
            return;
        }
        
        const newValueArray = Array.isArray(newValue) ? newValue : [newValue];
        const hasNothing = nothingStr && newValueArray.some(v => String(v) === nothingStr);
        const otherValues = newValueArray.filter(v => String(v) !== nothingStr);
        
        if (hasNothing && otherValues.length > 0) {
            // Wybrano "NIC NIE CHCĘ PODKREŚLAĆ" i coś innego → usuń "NIC NIE CHCĘ PODKREŚLAĆ"
            onChange(otherValues);
        } else if (otherValues.length === 0 && !hasNothing) {
            // Nic nie wybrano → ustaw "NIC NIE CHCĘ PODKREŚLAĆ" lub "ŻADNA"
            if (nothingOptionId !== null) {
                onChange([nothingOptionId]);
            }
        } else {
            const valueAsArray = Array.isArray(newValue) ? newValue : [newValue];
            onChange(valueAsArray);
        }
    };

    return (
        <SurveyAnswerCustomMap
            question={question}
            value={value}
            onChange={handleChange}
            onSave={onSave}
            svgPath={modelPartsSvg}
            mapConfig={mapConfig}
            ariaLabel="Mapa części ciała"
        />
    );
};

export default SurveyAnswerBodyParts;
